/**
 * Exclusive sidebar page ↔ notification type maps.
 * Each type belongs to at most one sidebar page so dots and mark-as-read stay aligned.
 * Dashboard Action Required types are intentionally excluded (resolved by action, not page visit).
 */
import { NOTIFICATION_TYPES } from './notificationTypes';

/** Firestore `in` operator max values per query. */
export const FIRESTORE_IN_QUERY_MAX = 10;

export type BrokerShipperSidebarPage =
  | 'purchaseOrders'
  | 'shippingSchedule'
  | 'driverUpdates'
  | 'carrierPartners'
  | 'payments'
  | 'shipmentArchive';

export type CarrierSidebarPage = 'myLoads' | 'availableLoads' | 'partners' | 'payments';

/**
 * Broker + shipper sidebar dots — exclusive ownership (no overlapping types).
 * Visit ack + mark-read must use this same map.
 */
export const BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES: Record<BrokerShipperSidebarPage, readonly string[]> = {
  purchaseOrders: [
    NOTIFICATION_TYPES.CARRIER_ACCEPT,
    NOTIFICATION_TYPES.CARRIER_ACCEPT_BROKER,
    NOTIFICATION_TYPES.LOAD_COMPLETED,
    NOTIFICATION_TYPES.LOAD_STATUS_UPDATE,
    NOTIFICATION_TYPES.LOAD_MODIFIED,
    NOTIFICATION_TYPES.LOAD_CANCELLED,
    NOTIFICATION_TYPES.CANCELLATION_APPROVED,
    NOTIFICATION_TYPES.CANCELLATION_REJECTED,
    NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
  ],
  shippingSchedule: [NOTIFICATION_TYPES.CARRIER_ASSIGNED, NOTIFICATION_TYPES.LOAD_REQUEST],
  driverUpdates: [
    NOTIFICATION_TYPES.LOAD_TRACKING_UPDATE,
    NOTIFICATION_TYPES.LOAD_STARTED,
    NOTIFICATION_TYPES.LOAD_OFF_ROUTE,
    NOTIFICATION_TYPES.LOAD_ACTIVE_FOR_TRACKING,
  ],
  carrierPartners: [NOTIFICATION_TYPES.PARTNER_REQUEST, NOTIFICATION_TYPES.PARTNERSHIP_CREATED],
  payments: [
    NOTIFICATION_TYPES.PAYMENT_RECEIVED,
    NOTIFICATION_TYPES.INVOICE_GENERATED,
    NOTIFICATION_TYPES.INVOICE_RECEIVED,
    NOTIFICATION_TYPES.TONU_INVOICE_CREATED,
    NOTIFICATION_TYPES.FACTORING_REQUESTED,
    NOTIFICATION_TYPES.PAYMENT_STATUS_UPDATED,
  ],
  shipmentArchive: [NOTIFICATION_TYPES.SHIPMENT_ARCHIVED],
};

export const BROKER_SHIPPER_BELL_ONLY_NOTIFICATION_TYPES: readonly string[] = [
  NOTIFICATION_TYPES.SYSTEM_ALERT,
  NOTIFICATION_TYPES.RATING_RECEIVED,
  NOTIFICATION_TYPES.MESSAGE_RECEIVED,
  NOTIFICATION_TYPES.MARKETPLACE,
];

export const CARRIER_SIDEBAR_NOTIFICATION_TYPES: Record<CarrierSidebarPage, readonly string[]> = {
  myLoads: [
    NOTIFICATION_TYPES.LOAD_REQUEST,
    NOTIFICATION_TYPES.LOAD_STATUS_UPDATE,
    NOTIFICATION_TYPES.LOAD_MODIFIED,
    NOTIFICATION_TYPES.LOAD_CANCELLED,
    NOTIFICATION_TYPES.CANCELLATION_REQUEST,
    NOTIFICATION_TYPES.LOAD_TRACKING_UPDATE,
    NOTIFICATION_TYPES.LOAD_STARTED,
    NOTIFICATION_TYPES.LOAD_COMPLETED,
    NOTIFICATION_TYPES.CARRIER_ASSIGNED,
    NOTIFICATION_TYPES.CARRIER_ACCEPTED,
    NOTIFICATION_TYPES.CARRIER_ACCEPT_BROKER,
    NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
  ],
  availableLoads: [
    NOTIFICATION_TYPES.MAKE_OFFER,
    NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
    NOTIFICATION_TYPES.SHIPPER_ACCEPT_COUNTER,
    NOTIFICATION_TYPES.SHIPPER_REJECT_COUNTER,
    NOTIFICATION_TYPES.MARKETPLACE_APPROVAL_RATE_CONFIRMATION,
  ],
  partners: [NOTIFICATION_TYPES.PARTNER_REQUEST, NOTIFICATION_TYPES.PARTNERSHIP_CREATED],
  payments: [
    NOTIFICATION_TYPES.PAYMENT_RECEIVED,
    NOTIFICATION_TYPES.INVOICE_GENERATED,
    NOTIFICATION_TYPES.INVOICE_RECEIVED,
    NOTIFICATION_TYPES.TONU_INVOICE_CREATED,
    NOTIFICATION_TYPES.FACTORING_REQUESTED,
    NOTIFICATION_TYPES.PAYMENT_STATUS_UPDATED,
    NOTIFICATION_TYPES.CHECK_PAYMENT_PENDING,
  ],
};

export function chunkForFirestoreInQuery<T>(values: readonly T[], size: number = FIRESTORE_IN_QUERY_MAX): T[][] {
  if (values.length === 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size) as T[]);
  }
  return chunks;
}

export function getBrokerShipperSidebarPageForType(type: string): BrokerShipperSidebarPage | null {
  for (const [page, types] of Object.entries(BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES) as [
    BrokerShipperSidebarPage,
    readonly string[],
  ][]) {
    if (types.includes(type)) return page;
  }
  return null;
}

export function getCarrierSidebarPageForType(type: string): CarrierSidebarPage | null {
  for (const [page, types] of Object.entries(CARRIER_SIDEBAR_NOTIFICATION_TYPES) as [
    CarrierSidebarPage,
    readonly string[],
  ][]) {
    if (types.includes(type)) return page;
  }
  return null;
}

export function getNotificationTypesForSidebarPage(pageType: string): string[] {
  if (pageType in BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES) {
    return [...BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES[pageType as BrokerShipperSidebarPage]];
  }
  if (pageType in CARRIER_SIDEBAR_NOTIFICATION_TYPES) {
    return [...CARRIER_SIDEBAR_NOTIFICATION_TYPES[pageType as CarrierSidebarPage]];
  }
  return [];
}

function dateMillis(value: Date | null | undefined): number {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return Number.NEGATIVE_INFINITY;
  return value.getTime();
}

/** Keep the later of two visit timestamps (optimistic local wins over stale server). */
export function laterVisitDate(a: Date | null | undefined, b: Date | null | undefined): Date | null {
  const aMs = dateMillis(a);
  const bMs = dateMillis(b);
  if (aMs === Number.NEGATIVE_INFINITY && bMs === Number.NEGATIVE_INFINITY) return null;
  return aMs >= bMs ? (a as Date) : (b as Date);
}

/**
 * Merge remote pageVisits into local without regressing timestamps already advanced by markPageVisited.
 */
export function mergePageVisitsPreferNewer<T extends Record<string, Date | null | undefined>>(
  local: T | null | undefined,
  remote: T | null | undefined
): T {
  const keys = new Set([...Object.keys(local || {}), ...Object.keys(remote || {})]);
  const out: Record<string, Date | null> = {};
  for (const key of keys) {
    out[key] = laterVisitDate(local?.[key] ?? null, remote?.[key] ?? null);
  }
  return out as T;
}

/** Normalize Firestore createdAt; missing/invalid → epoch so it never looks "newer than visit". */
export function normalizeNotificationCreatedAt(value: unknown): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value && typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
    } catch {
      /* fall through */
    }
  }
  return new Date(0);
}
